<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-header">
        <h2>后台管理系统</h2>
        <p>欢迎登录</p>
      </div>
      
      <el-form
        ref="loginForm"
        :model="loginForm"
        :rules="loginRules"
        class="login-form"
        @submit.native.prevent="handleLogin"
      >
        <el-form-item prop="mobile">
          <el-input
            v-model="loginForm.mobile"
            placeholder="请输入手机号"
            prefix-icon="el-icon-user"
            clearable
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            show-password
            clearable
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        
        <el-form-item prop="code" v-if="needCode">
          <div class="code-input">
            <el-input
              v-model="loginForm.code"
              placeholder="请输入验证码"
              prefix-icon="el-icon-key"
              clearable
              @keyup.enter="handleLogin"
            />
            <el-button
              type="primary"
              :disabled="codeCountdown > 0"
              @click="sendCode"
              class="code-btn"
            >
              {{ codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>
        
        <el-form-item>
          <el-button
            type="primary"
            :loading="loading"
            class="login-btn"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { login, sendSmsCode } from '../api/auth'

export default {
  name: 'Login',
  data() {
    // 手机号验证
    const validateMobile = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入手机号'))
      } else if (!/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号'))
      } else {
        callback()
      }
    }
    
    return {
      loading: false,
      needCode: false, // 是否需要验证码
      codeCountdown: 0,
      codeTimer: null,
      loginForm: {
        mobile: '',
        password: '',
        code: ''
      },
      loginRules: {
        mobile: [
          { required: true, validator: validateMobile, trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' }
        ]
      }
    }
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
  },
  methods: {
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate()
        
        this.loading = true
        
        const params = {
          mobile: this.loginForm.mobile,
          password: this.loginForm.password
        }
        
        // 如果需要验证码，添加验证码参数
        if (this.needCode) {
          params.code = this.loginForm.code
        }
        
        const response = await login(params)

        if (response.code === 200) {
          this.$message.success('登录成功')

          // 存储登录信息
          localStorage.setItem('token', response.data.token)
          localStorage.setItem('userInfo', JSON.stringify(response.data))

          // 跳转到首页
          this.$router.push('/dashboard')
        } else {
          this.handleLoginError(response)
        }
      } catch (error) {
        // utils/request.js 的响应拦截器在 code !== 200 时会 reject，
        // 所以「密码错误」这类业务失败是从这里抛出来的，
        // 不是走上面那个 else 分支。之前的写法把真实原因吞掉了，
        // 只会弹一个「登录失败，请稍后重试」，让人以为是接口挂了。
        console.error('登录错误:', error)

        // 只有业务错误（响应体本身就是 {code, msg, data}）才在这里补提示；
        // HTTP 层的错误（401/500）request.js 的拦截器已经弹过提示并跳转了，
        // 别再弹一次
        if (error && error.code && error.msg) {
          this.handleLoginError(error)
        }
      } finally {
        this.loading = false
      }
    },

    // 统一处理登录失败：把后端返回的真实原因显示出来
    handleLoginError(res) {
      const msg = (res && res.msg) || '登录失败'
      const code = res && res.code

      this.$message.error(msg)

      // 后端说需要验证码，就把验证码输入框放出来
      if (code === 401 && msg.indexOf('验证码') !== -1) {
        this.needCode = true
      }
    },
    
    async sendCode() {
      try {
        await this.$refs.loginForm.validateField('mobile')
        
        const response = await sendSmsCode({
          mobile: this.loginForm.mobile
        })

        if (response.code === 200) {
          this.$message.success('验证码发送成功')
          this.startCountdown()

          // 开发环境后端会直接把验证码回传，方便自测
          if (response.data && response.data.code) {
            this.$message.info('调试验证码：' + response.data.code)
          }
        } else {
          this.$message.error(response.msg || '验证码发送失败')
        }
      } catch (error) {
        console.error('发送验证码错误:', error)
        // 同上：业务错误被拦截器 reject 掉了，真实原因要从 error 上取
        if (error && error.code && error.msg) {
          this.$message.error(error.msg)
        }
      }
    },
    
    startCountdown() {
      this.codeCountdown = 60
      this.codeTimer = setInterval(() => {
        this.codeCountdown--
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer)
          this.codeTimer = null
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.login-box {
  width: 400px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h2 {
  color: #333;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 10px;
}

.login-header p {
  color: #666;
  font-size: 14px;
}

.login-form {
  margin-top: 20px;
}

.code-input {
  display: flex;
  gap: 10px;
}

.code-input .el-input {
  flex: 1;
}

.code-btn {
  width: 120px;
  flex-shrink: 0;
}

.login-btn {
  width: 100%;
  height: 45px;
  font-size: 16px;
  border-radius: 5px;
}

.el-form-item {
  margin-bottom: 20px;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 30px 20px;
  }
  
  .code-input {
    flex-direction: column;
  }
  
  .code-btn {
    width: 100%;
  }
}
</style>